import type React from 'react';
import type { ToolImage, ImageLabel, LabelingState, HistoryEntry } from '../types/LabelingTypes';
import type { HttpClient } from '@/lib/http/HttpClient';
import {
  labelizeClassify,
  labelizeClassifyBatch,
} from '../api/LabelingApiClient';
import {
  applyLabelReducer,
  mapCnnLabel,
  storeCnnSuggestion,
} from './labelingReducer';
import { saveAutoAdvance } from '../storage/labelingStorage';

// ---------- initial state ----------

export const INITIAL_STATE: LabelingState = {
  items: [],
  currentKey: '',
  mode: 'focus',
  statusFilter: 'all',
  seriesFilter: 'all',
  selected: new Set(),
  autoAdvance: true,
  history: [],
};

// ---------- helpers ----------

export function computeFiltered(
  items: ToolImage[],
  statusFilter: 'all' | 'todo' | 'done',
  seriesFilter: string
): ToolImage[] {
  return items.filter((item) => {
    const statusOk =
      statusFilter === 'all' ||
      (statusFilter === 'todo' ? item.label === null : item.label !== null);
    const seriesOk = seriesFilter === 'all' || item.toolId === seriesFilter;
    return statusOk && seriesOk;
  });
}

// ---------- action implementation types ----------

type SetItems = React.Dispatch<React.SetStateAction<ToolImage[]>>;
type SetHistory = React.Dispatch<React.SetStateAction<HistoryEntry[]>>;
type SetCurrentKey = React.Dispatch<React.SetStateAction<string>>;

// ---------- action implementations ----------

export function applyLabelAction(
  key: string,
  state: ImageLabel,
  setItems: SetItems,
  setHistory: SetHistory,
  setCurrentKey: SetCurrentKey,
  autoAdvanceRef: React.MutableRefObject<boolean>,
  modeRef: React.MutableRefObject<'focus' | 'grid' | 'dichotomous'>,
  statusFilterRef: React.MutableRefObject<'all' | 'todo' | 'done'>,
  seriesFilterRef: React.MutableRefObject<string>,
  onSaveLabels?: (items: ToolImage[]) => void
): void {
  setItems((prev) => {
    const [newItems, entry] = applyLabelReducer(prev, key, state);
    setHistory((prevH) => {
      const newHistory = [...prevH, entry];
      onSaveLabels?.(newItems);
      return newHistory;
    });

    if (autoAdvanceRef.current && modeRef.current === 'focus') {
      setTimeout(() => {
        setCurrentKey((prevKey) => {
          const filteredNow = computeFiltered(
            newItems,
            statusFilterRef.current,
            seriesFilterRef.current
          );
          const currentIdx = filteredNow.findIndex((i) => i.key === prevKey);
          const afterCurrent = filteredNow.slice(currentIdx + 1);
          const nextUnlabeled = afterCurrent.find((i) => i.label === null);
          if (nextUnlabeled) return nextUnlabeled.key;
          const nextItem = filteredNow[currentIdx + 1];
          if (nextItem) return nextItem.key;
          return prevKey;
        });
      }, 200);
    }

    return newItems;
  });
}

export function undoAction(
  setHistory: SetHistory,
  setItems: SetItems,
  setCurrentKey: SetCurrentKey,
  onSyncUndo?: (key: string, label: ImageLabel | null) => void
): void {
  setHistory((prevH) => {
    if (prevH.length === 0) return prevH;
    const newHistory = prevH.slice(0, -1);
    const entry = prevH[prevH.length - 1];
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.key === entry.key
          ? { ...item, label: entry.label }
          : item
      )
    );
    setCurrentKey(entry.key);
    onSyncUndo?.(entry.key, entry.label);
    return newHistory;
  });
}

export function bulkLabelAction(
  state: ImageLabel,
  setSelected: React.Dispatch<React.SetStateAction<Set<string>>>,
  setItems: SetItems,
  setHistory: SetHistory,
  onSaveLabels?: (items: ToolImage[]) => void
): void {
  setSelected((prevSelected) => {
    const keys = Array.from(prevSelected);
    setItems((prevItems) => {
      let newItems = prevItems;
      const entries: HistoryEntry[] = [];
      for (const key of keys) {
        const [updated, entry] = applyLabelReducer(newItems, key, state);
        newItems = updated;
        entries.push(entry);
      }
      setHistory((prevH) => {
        const newHistory = [...prevH, ...entries];
        onSaveLabels?.(newItems);
        return newHistory;
      });
      return newItems;
    });
    return new Set<string>();
  });
}

/**
 * Applies an explicit list of key→label pairs in one pass. Unlike `bulkLabelAction`
 * the keys come from the caller (the dichotomous search plan) rather than from the
 * grid selection, and the result IS synced to the server via `onSaveLabels`.
 * No auto-advance: navigation is owned by the caller, not by this write.
 */
export function applyLabelsBulkAction(
  entries: Array<{ key: string; label: ImageLabel }>,
  setItems: SetItems,
  setHistory: SetHistory,
  onSaveLabels?: (items: ToolImage[]) => void
): void {
  setItems((prevItems) => {
    let newItems = prevItems;
    const historyEntries: HistoryEntry[] = [];
    for (const { key, label } of entries) {
      const [updated, entry] = applyLabelReducer(newItems, key, label);
      newItems = updated;
      historyEntries.push(entry);
    }
    setHistory((prevH) => {
      const newHistory = [...prevH, ...historyEntries];
      onSaveLabels?.(newItems);
      return newHistory;
    });
    return newItems;
  });
}

export function setAutoAdvanceAction(
  b: boolean,
  setAutoAdvanceState: React.Dispatch<React.SetStateAction<boolean>>,
  autoAdvanceRef: React.MutableRefObject<boolean>
): void {
  setAutoAdvanceState(b);
  autoAdvanceRef.current = b;
  saveAutoAdvance(b);
}

export async function classifyWithAiAction(
  key: string,
  setItems: SetItems,
  http: HttpClient | null
): Promise<void> {
  if (!http) return;
  let result;
  try {
    result = await labelizeClassify(http, key);
  } catch (err) {
    // Backend down or model weights not mounted (typed 503) — degrade
    // gracefully instead of surfacing an unhandled rejection on click.
    console.warn('AI classification unavailable:', err);
    return;
  }
  const mappedState = mapCnnLabel(result.label);
  const suggestion = {
    label: result.label,
    mappedState,
    confidence: result.confidence,
    at: new Date().toISOString(),
  };
  setItems((prev) => storeCnnSuggestion(prev, key, suggestion));
}

export async function bulkClassifyWithAiAction(
  selected: Set<string>,
  setItems: SetItems,
  setSelected: React.Dispatch<React.SetStateAction<Set<string>>>,
  http: HttpClient | null
): Promise<void> {
  if (!http) return;
  const keys = Array.from(selected);
  let results;
  try {
    results = await labelizeClassifyBatch(http, keys);
  } catch (err) {
    console.warn('AI classification unavailable:', err);
    return;
  }
  setItems((prevItems) => {
    let newItems = prevItems;
    for (const result of results) {
      const mappedState = mapCnnLabel(result.label);
      const suggestion = {
        label: result.label,
        mappedState,
        confidence: result.confidence,
        at: new Date().toISOString(),
      };
      newItems = storeCnnSuggestion(newItems, result.key, suggestion);
    }
    // Advisory-only, same semantics as single classify: suggestions never auto-apply
    // labels — the user accepts them via Accept / Accept-all — so the undo history is
    // deliberately left untouched here.
    return newItems;
  });
  setSelected(new Set());
}
